// deps
import React, { useCallback, useContext, useMemo } from "react";

// context
import { EmiSelectionContext } from "@payment-client/contexts/EmiSelectionContext";

// components
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";
import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";

// styles
import styles from "./styles.scss";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";
import { IEMITenure } from "@swiggy-private/dls-web-recipe-common-web/dist/components/EMICard";
import { IEMIBankPlan } from "@swiggy-private/dls-web-recipe-common-web/dist/components/EMICard";

// components
import { EMIPlanList, EMIListLoader } from "@swiggy-private/payment-ui";
import { GenericError, useEffectOnce } from "@swiggy-private/core-ui";

// utils
import { useOnBack } from "@swiggy-private/core-utils";

// constants
import { EMI_PAGE_TITLE, EMI_ERROR } from "./constant";

// utils
import {
    useNavigate,
    CheckoutRoute,
    AnalyticsContext,
    GlobalHeaderState,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";

// analytics
import {
    onEmiProceedClick,
    onEmiExpandClick,
    onEmiTncClick,
    onEmiSelectionScreenView,
    onEmiOptionClick,
} from "@payment-client/analytics/events/EMISelection";

// contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";

type IEMISelectionProps = RouteProps<{
    cardReference?: string;
}>;

export function EmiSelection(props: IEMISelectionProps): React.ReactElement {
    const { emiError, emiResponse, loadingEmi, updateEmiSelection, fetchEmiDetails } = useContext(EmiSelectionContext);
    const { paymentResponse } = useContext(ShowPaymentContext);
    const { billTotal, withSplitPay } = useContext(PaymentPayContext);
    const onBack = useOnBack();
    const navigateTo = useNavigate();
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);

    const { cardReference } = props.data ?? {};
    const header = useMemo(() => <CommonHeaderContent title={EMI_PAGE_TITLE} grayscale />, []);

    useEffectOnce(() => {
        fetchEmiDetails(cardReference);
    });

    useEffectOnce(() => {
        const cartId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.cartId) ?? "";
        const planId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.planid) ?? "";

        onEmiSelectionScreenView(impressionEvent, {
            cartId,
            planId,
            couponCode: paymentResponse?.couponCode,
            billTotal,
            withSplitPay,
            couponApplied: paymentResponse?.couponApplied,
        });
    });

    const onProceed = useCallback(
        (emiPlan: IEMIBankPlan, tenure: IEMITenure) => {
            updateEmiSelection({
                tenure: tenure.tenure,
                bankCode: emiPlan.bankCode,
                emiBank: emiPlan.emiBank,
                bankName: emiPlan.bankName,
                emiType: tenure.emiType,
                txnEmiType: tenure.txnEmiType,
                cardReference: emiPlan?.savedCardDetails?.token?.cardReference ?? null,
            });
            onEmiProceedClick(clickEvent);

            navigateTo(CheckoutRoute.REVIEW_ORDER_DETAILS);
        },
        [updateEmiSelection, clickEvent, navigateTo, onEmiProceedClick],
    );

    const onEMIExpandClickEvent = useCallback(() => {
        onEmiExpandClick(clickEvent);
    }, [clickEvent]);

    const onEMITncClickEvent = useCallback(() => {
        onEmiTncClick(clickEvent);
    }, [clickEvent]);

    const onEmiOptionClickEvent = useCallback(() => {
        onEmiOptionClick(clickEvent);
    }, [clickEvent]);

    const emiContent = useMemo(
        () => (
            <EMIPlanList
                emiPlans={emiResponse?.emiPlans ?? []}
                savedCardEmiPlans={emiResponse?.savedCardEmiPlans ?? []}
                onProceedClick={onProceed}
                onEMIExpandClick={onEMIExpandClickEvent}
                onEMITncClick={onEMITncClickEvent}
                onEmiOptionClick={onEmiOptionClickEvent}
            />
        ),
        [
            emiResponse?.emiPlans,
            emiResponse?.savedCardEmiPlans,
            onProceed,
            onEMIExpandClickEvent,
            onEMITncClickEvent,
            onEmiOptionClickEvent,
        ],
    );

    if (loadingEmi) {
        return <CommonUIWrapper header={header} content={<EMIListLoader />} containerCSSClass={styles.container} />;
    }

    if (emiError) {
        return (
            <CommonUIWrapper
                header={header}
                content={
                    <GenericError
                        title={EMI_ERROR.TITLE}
                        message={EMI_ERROR.MESSAGE}
                        action={{ text: EMI_ERROR.ACTION, onClick: onBack }}
                    />
                }
            />
        );
    }

    return <CommonUIWrapper header={header} content={emiContent} containerCSSClass={styles.container} />;
}
